import React, { useState, useEffect, useRef, cloneElement, isValidElement } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { Helmet } from 'react-helmet-async';
import loadable from '@loadable/component';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getTitleAfterHyphen } from '../../util/string';
import {
  OFFER,
  REQUEST,
  isBookingProcess,
  isNegotiationProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

// Shared components
import {
  H1,
  H2,
  H4,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Modal,
  PrimaryButton,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleNavigateToMakeOfferPage,
  handleNavigateToRequestQuotePage,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import ActionBarMaybe from './ActionBarMaybe';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';
import renderMarkdown from '../PageBuilder/markdownProcessor';

import { optimizeCloudinaryUrl, getCloudinarySrcSet, DESKTOP_HERO_SRCSET_WIDTHS, TABLET_SRCSET_WIDTHS, MOBILE_SRCSET_WIDTHS } from '../../util/cloudinaryHelpers';

const SectionReviews = loadable(() => import('./SectionReviews'));
const SectionMapMaybe = loadable(() => import('./SectionMapMaybe'));
const HistoryCarousel = loadable(() => import('./HistoryCarousel/HistoryCarousel'));
const GoldenScreenGallery = loadable(() => import('./GoldenScreenGallery/GoldenScreenGallery'));

import css from './ListingPage.module.css';
import StreamVideo from '../../components/CustomBuild/StreamVideo/StreamVideo';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard.js';
import ScrollDown from '../../components/CustomBuild/InteractiveCursor/ScrollDown.js';
import SmoothScrollWrapper, {
  LENIS_READY_EVENT,
} from '../../components/GsapAnimation/SmoothScrollWrapper/SmoothScrollWrapper.js';
import { useLenis } from '@studio-freight/react-lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}
import {
  HeroInfoReveal,
  HeroTitleReveal,
} from '../../components/GsapAnimation/HeroTitleReveal/HeroTitleReveal.js';
import HeroStatsRevealStandalone from '../../components/GsapAnimation/HeroStatsRevealStandalone/HeroStatsRevealStandalone.js';
import ParagraphRevealStandalone from '../../components/GsapAnimation/ParagraphRevealStandalone/ParagraphRevealStandalone.js';
import { useMagneticCta } from '../../components/GsapAnimation/CustomIdAnimate/MagneticIdAnimation.js';
import {
  MIXPANEL_EVENTS,
  listingMixpanelProperties,
  trackMixpanelEvent,
} from '../../util/mixpanel';

/**
 * MagneticCtaButton
 * Isolated component so useMagneticCta's useEffect only runs when
 * this subtree is actually mounted — refs are always populated.
 */
const MagneticCtaButton = ({
  listingSlug,
  listingId,
  isGoldenScreen,
  css: cssModule,
  onClick,
  className,
}) => {
  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const ringRef = useRef(null);

  useMagneticCta(wrapperRef, btnRef, ringRef, {
    btnStrength: 0.12,
    ringStrength: 0.06,
    moveDuration: 1.0,
    returnDuration: 1.6,
  });

  return (
    <div ref={wrapperRef} className={classNames(cssModule.ctaBtnWrapper, className)}>
      <div ref={ringRef} className={cssModule.outlineContainer} />
      <div ref={btnRef} style={{ display: 'inline-block', willChange: 'transform' }}>
        <NamedLink
          name={isGoldenScreen ? 'ListingPage' : 'BookingFlowPage'}
          params={{ slug: listingSlug, id: listingId }}
          to={isGoldenScreen ? { search: '?showDetails=true' } : undefined}
          onClick={onClick}
          className={classNames(cssModule.experienceCtaLink, {
            [cssModule.goldenScreenExperienceCtaLink]: isGoldenScreen,
            [cssModule.nonGoldenScreenExperienceCtaLink]: !isGoldenScreen,
          })}
        >
          <span
            className={classNames(cssModule.experienceCtaLabel, {
              [cssModule.goldenScreenExperienceCtaLabel]: isGoldenScreen,
              [cssModule.nonGoldenScreenExperienceCtaLabel]: !isGoldenScreen,
            })}
          >
            {isGoldenScreen ? (
              <FormattedMessage id="ListingPageCarousel.experienceCtaLabelGoldenScreen" />
            ) : (
              <FormattedMessage id="ListingPageCarousel.experienceCtaLabelNonGoldenScreen" />
            )}
          </span>
          <span
            className={classNames(cssModule.experienceCtaArrow, {
              [cssModule.goldenScreenExperienceCtaArrow]: isGoldenScreen,
              [cssModule.nonGoldenScreenExperienceCtaArrow]: !isGoldenScreen,
            })}
            aria-hidden="true"
          >
            <IconCard brand="rightArrow" />
          </span>
        </NamedLink>
      </div>
    </div>
  );
};

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;
const NON_GOLDEN_SCREEN_CTA_BACKGROUND = '/static/img/bg-cover/non-golden-bg-exp.png';
const ENABLE_GOLDEN_SCREEN_HARD_SCROLL = false;
const GOLDEN_SCREEN_HARD_SCROLL_DISTANCE = '+=20%'; // NOTE: change to 40 if asked too
const META_DESCRIPTION_MAX_LENGTH = 160;

const { UUID } = sdkTypes;

// ---------------------------------------------------------------------------
// SEO helpers
// ---------------------------------------------------------------------------

/**
 * Strip HTML/markdown-ish tags and collapse whitespace so raw listing
 * `description` text is safe to drop into <meta description>, OG tags,
 * and JSON-LD without leaking markup or double-spacing.
 */
const stripHtml = str =>
  (str || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const applyParagraphClass = (node, className) => {
  if (node == null || typeof node === 'boolean') {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map(child => applyParagraphClass(child, className));
  }
  if (!isValidElement(node)) {
    return node;
  }
  if (node.type === 'p') {
    return cloneElement(node, {
      className: classNames(node.props.className, className),
    });
  }
  if (node.props.children == null) {
    return node;
  }
  return cloneElement(node, null, applyParagraphClass(node.props.children, className));
};

const listingMarkup = (text, paragraphClassName) => {
  if (!text) return null;
  return applyParagraphClass(renderMarkdown(text), paragraphClassName);
};

/**
 * Build a clean, length-capped meta description. Falls back to an
 * intl-provided default when the listing has no real description, so we
 * never fall through to an unknown/lorem-ipsum fallback further down
 * the rendering chain.
 */
const buildMetaDescription = (
  rawDescription,
  fallbackText,
  maxLength = META_DESCRIPTION_MAX_LENGTH
) => {
  const clean = stripHtml(rawDescription);
  const base = clean || stripHtml(fallbackText) || '';
  if (!base) return '';
  return base.length > maxLength ? `${base.slice(0, maxLength - 1).trim()}…` : base;
};

const getSavedTravelPreference = (currentUser, listingId) => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const beenHereListingIds = Array.isArray(publicData.beenHereListingIds)
    ? publicData.beenHereListingIds
    : [];
  const wantToGoListingIds = Array.isArray(publicData.wantToGoListingIds)
    ? publicData.wantToGoListingIds
    : [];

  return beenHereListingIds.includes(listingId)
    ? 'been_here'
    : wantToGoListingIds.includes(listingId)
      ? 'want_to_go'
      : null;
};

export const ListingPageComponent = props => {
  const lenis = useLenis();
  const queryParams = new URLSearchParams(props.location?.search || '');
  const showDetails = queryParams.get('showDetails') === 'true';

  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params?.id
  );
  const [mounted, setMounted] = useState(false);
  const [orderPanelModalOpen, setOrderPanelModalOpen] = useState(false);
  const [travelPreference, setTravelPreference] = useState(() =>
    getSavedTravelPreference(props.currentUser, props.params?.id)
  );
  const [travelPreferenceSaveInProgress, setTravelPreferenceSaveInProgress] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  const [activeHoverCard, setActiveHoverCard] = useState(false);
  const [pricingHoverDismissed, setPricingHoverDismissed] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const handleMouseEnterPricingCard = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setPricingHoverDismissed(false);
    setActiveHoverCard(true);
  };

  const handleMouseLeaveHoverCard = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveHoverCard(false);
    }, 150);
  };

  const handleClosePricingHoverCard = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveHoverCard(false);
    setPricingHoverDismissed(true);
  };

  const handleTogglePricingHoverCard = event => {
    if (event?.target?.closest?.('a')) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setPricingHoverDismissed(false);
    setActiveHoverCard(prev => !prev);
  };

  useEffect(() => {
    setMounted(true);
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setTravelPreference(getSavedTravelPreference(props.currentUser, props.params?.id));
  }, [props.currentUser, props.params?.id]);

  // Lenis keeps its virtual scroll offset across in-app navigation, and
  // ScrollTrigger.refresh() re-applies remembered positions through the Lenis
  // proxy. Without an explicit reset, arriving from another page (e.g. the
  // "Discover" card link) opens the listing mid-page — at the description
  // reveal instead of the golden-screen hero.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // An anchor in the URL is an intentional scroll target — leave it alone.
    if (props.location?.hash) return undefined;

    const resetToTop = () => {
      ScrollTrigger.clearScrollMemory();
      if (lenis) {
        lenis.stop();
        lenis.scrollTo(0, { immediate: true, force: true });
        lenis.start();
      }
      window.scrollTo(0, 0);
      ScrollTrigger.refresh();
    };

    resetToTop();
    const rafId = window.requestAnimationFrame(resetToTop);

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [props.params?.id, props.location?.search, props.location?.hash, lenis]);

  // Keep ScrollTrigger start/end positions in sync after Lenis, images, and
  // carousel layout settle. Opening DevTools used to "fix" this by forcing
  // a resize/reflow — refresh proactively instead.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let timeoutId = null;
    const refresh = () => {
      ScrollTrigger.refresh();
    };

    const onLenisReady = () => refresh();
    window.addEventListener(LENIS_READY_EVENT, onLenisReady);
    window.addEventListener('load', refresh);

    // Deferred passes catch late layout shifts (hero image, HistoryCarousel).
    timeoutId = window.setTimeout(refresh, 100);
    const timeoutId2 = window.setTimeout(refresh, 600);
    const timeoutId3 = window.setTimeout(refresh, 1500);

    if (document.fonts?.ready) {
      document.fonts.ready.then(refresh).catch(() => { });
    }

    return () => {
      window.removeEventListener(LENIS_READY_EVENT, onLenisReady);
      window.removeEventListener('load', refresh);
      window.clearTimeout(timeoutId);
      window.clearTimeout(timeoutId2);
      window.clearTimeout(timeoutId3);
    };
  }, [showDetails, windowWidth, props.params?.id]);

  // Ref for the golden-screen hero content that fades out on scroll.
  // Declared here (before any early returns) to satisfy Rules of Hooks.
  // The effect body guards against non-golden-screen listings internally.
  const goldenScreenTitlteHide = useRef(null);
  const goldenScreenHeroWrapper = useRef(null);
  const goldenScreenVideo = useRef();
  const goldenScreenContentRef = useRef(null);
  const experiencePriceRef = useRef(null);
  const videoStartedRef = useRef(false);
  const videoProgressRef = useRef(new Set());
  const funnelStartedAtRef = useRef(null);
  const experiencePriceViewedRef = useRef(false);
  const experienceContinuedRef = useRef(false);
  const priceAbandonmentTrackedRef = useRef(false);
  const goldenScreenViewedRef = useRef(false);
  const experienceDetailOpenedRef = useRef(false);
  const listingAbandonmentTrackedRef = useRef(false);

  useEffect(() => {
    if (!activeHoverCard || typeof document === 'undefined') return undefined;

    const handlePointerDownOutside = event => {
      const priceSection = experiencePriceRef.current;
      if (priceSection && priceSection.contains(event.target)) return;
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      setActiveHoverCard(false);
    };

    document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [activeHoverCard]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let tl = null;
    let videoContext = null;
    let hardScrollTrigger = null;
    let playPauseTrigger = null;

    const init = () => {
      const el = goldenScreenTitlteHide.current;
      const videoEl = goldenScreenVideo.current;
      const heroEl = goldenScreenHeroWrapper.current;
      if (!el && !videoEl) return; // not mounted (non-golden listing) — safe no-op

      tl?.scrollTrigger?.kill();
      tl?.kill();
      videoContext?.revert();
      hardScrollTrigger?.kill();

      if (el) {
        gsap.killTweensOf(el);
        gsap.set(el, { clearProps: 'opacity,transform,y' });

        tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            start: '0 10%',
            end: 'bottom 80%',
            scrub: 1.4,
            invalidateOnRefresh: true,
            once: false,
            markers: false,
          },
        });

        tl.to(el, { opacity: 0, y: -30, ease: 'power1.inOut' });
      }

      if (videoEl) {
        gsap.killTweensOf(videoEl);

        const videoTrigger = heroEl || videoEl;
        const playVideo = () => {
          const playPromise = videoEl.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => { });
          }
        };
        const resetVideo = () => {
          videoEl.pause();
          videoEl.currentTime = 0;
        };
        const pauseVideo = () => {
          videoEl.pause();
        };

        videoContext = gsap.context(() => {
          gsap.set(videoEl, {
            autoAlpha: 0,
          });

          gsap.to(videoEl, {
            autoAlpha: 1,
            ease: 'power2.inOut',
            scrollTrigger: {
              trigger: videoTrigger,
              start: ENABLE_GOLDEN_SCREEN_HARD_SCROLL ? 'top top' : 'top 80%',
              end: ENABLE_GOLDEN_SCREEN_HARD_SCROLL
                ? GOLDEN_SCREEN_HARD_SCROLL_DISTANCE
                : 'top 40%',
              scrub: 1.4,
              markers: false,
              once: true,
              once: true,
              invalidateOnRefresh: true,
              onEnter: playVideo,
              onEnterBack: playVideo,
              onLeaveBack: resetVideo,
              onUpdate: self => {
                if (self.progress > 0 && videoEl.paused) {
                  playVideo();
                }
              },
            },
          });
        }, videoTrigger);

        // Dedicated play/pause trigger — fires every time the video enters/leaves
        // the viewport so playback tracks scroll position rather than autoplaying.
        playPauseTrigger = ScrollTrigger.create({
          trigger: videoTrigger,
          start: 'top bottom',   // video top crosses the viewport bottom (entering)
          end: 'bottom top',     // video bottom crosses the viewport top (leaving)
          invalidateOnRefresh: true,
          onEnter: playVideo,
          onEnterBack: playVideo,
          onLeave: pauseVideo,
          onLeaveBack: pauseVideo,
        });

        if (ENABLE_GOLDEN_SCREEN_HARD_SCROLL && heroEl) {
          hardScrollTrigger = ScrollTrigger.create({
            trigger: heroEl,
            start: 'top top',
            end: GOLDEN_SCREEN_HARD_SCROLL_DISTANCE,
            pin: true,
            pinSpacing: true,
            scrub: .5,
            anticipatePin: .5,
            invalidateOnRefresh: true,
          });
        }
      }

      ScrollTrigger.refresh();
    };

    window.addEventListener(LENIS_READY_EVENT, init, { once: true });
    const fallback = setTimeout(init, 300);

    return () => {
      window.removeEventListener(LENIS_READY_EVENT, init);
      clearTimeout(fallback);
      tl?.scrollTrigger?.kill();
      tl?.kill();
      videoContext?.revert();
      hardScrollTrigger?.kill();
      playPauseTrigger?.kill();
    };
  }, [windowWidth, showDetails, props.params?.id]); // rerun when the responsive video element changes, showDetails transitions, or listing ID changes

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    onUpdateProfile,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));
  const trackingListingId = currentListing?.id?.uuid;
  const trackingListingTitle = currentListing?.attributes?.title;
  const trackingPublicData = currentListing?.attributes?.publicData || {};
  const trackingIsGoldenScreen =
    trackingPublicData.screen_experience === 'golden_screen' && !showDetails;
  const trackingListingProperties = listingMixpanelProperties(currentListing);

  const handleTravelPreferenceSelect = async preference => {
    if (travelPreferenceSaveInProgress) {
      return;
    }

    const previousPreference = travelPreference;
    const eventProperties = {
      ...trackingListingProperties,
      preference,
      is_authenticated: Boolean(currentUser?.id?.uuid),
      source: 'listing_hero',
    };

    trackMixpanelEvent(
      preference === 'been_here'
        ? MIXPANEL_EVENTS.LISTING_BEEN_HERE_SELECTED
        : MIXPANEL_EVENTS.LISTING_WANT_TO_GO_SELECTED,
      eventProperties
    );

    if (previousPreference === preference) {
      return;
    }

    setTravelPreference(preference);

    if (!currentUser?.id?.uuid || !trackingListingId) {
      return;
    }

    const currentPublicData = currentUser.attributes?.profile?.publicData || {};
    const currentBeenHereListingIds = Array.isArray(currentPublicData.beenHereListingIds)
      ? currentPublicData.beenHereListingIds
      : [];
    const currentWantToGoListingIds = Array.isArray(currentPublicData.wantToGoListingIds)
      ? currentPublicData.wantToGoListingIds
      : [];
    const nextBeenHereListingIds =
      preference === 'been_here'
        ? Array.from(new Set([...currentBeenHereListingIds, trackingListingId]))
        : currentBeenHereListingIds.filter(id => id !== trackingListingId);
    const nextWantToGoListingIds =
      preference === 'want_to_go'
        ? Array.from(new Set([...currentWantToGoListingIds, trackingListingId]))
        : currentWantToGoListingIds.filter(id => id !== trackingListingId);

    setTravelPreferenceSaveInProgress(true);
    try {
      await onUpdateProfile({
        publicData: {
          ...currentPublicData,
          beenHereListingIds: nextBeenHereListingIds,
          wantToGoListingIds: nextWantToGoListingIds,
        },
      });
    } catch (error) {
      setTravelPreference(previousPreference);
      trackMixpanelEvent(MIXPANEL_EVENTS.LISTING_TRAVEL_PREFERENCE_SAVE_FAILED, {
        ...eventProperties,
        error_status: error?.status || null,
      });
    } finally {
      setTravelPreferenceSaveInProgress(false);
    }
  };

  useEffect(() => {
    if (!trackingListingId || !trackingListingTitle) {
      return;
    }

    funnelStartedAtRef.current =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    videoStartedRef.current = false;
    videoProgressRef.current = new Set();
    experiencePriceViewedRef.current = false;
    experienceContinuedRef.current = false;
    priceAbandonmentTrackedRef.current = false;
    goldenScreenViewedRef.current = false;
    experienceDetailOpenedRef.current = false;
    listingAbandonmentTrackedRef.current = false;

    trackMixpanelEvent(MIXPANEL_EVENTS.LISTING_VIEWED, {
      ...trackingListingProperties,
      view: showDetails ? 'experience_detail' : 'listing',
    });
  }, [trackingListingId, trackingListingTitle, showDetails]);

  useEffect(() => {
    if (!trackingListingId || !trackingListingTitle) {
      return undefined;
    }

    let accumulatedActiveMilliseconds = 0;
    let activeStartedAt = document.hidden
      ? null
      : typeof performance !== 'undefined'
        ? performance.now()
        : Date.now();
    let durationTracked = false;

    const pauseActiveTimer = () => {
      if (activeStartedAt === null) {
        return;
      }
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      accumulatedActiveMilliseconds += now - activeStartedAt;
      activeStartedAt = null;
    };

    const resumeActiveTimer = () => {
      if (activeStartedAt === null) {
        activeStartedAt =
          typeof performance !== 'undefined' ? performance.now() : Date.now();
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseActiveTimer();
      } else {
        resumeActiveTimer();
      }
    };

    const trackListingDuration = exitTrigger => {
      if (durationTracked) {
        return;
      }
      durationTracked = true;
      pauseActiveTimer();
      const durationSeconds = Math.round(accumulatedActiveMilliseconds / 1000);

      trackMixpanelEvent(MIXPANEL_EVENTS.LISTING_PAGE_DURATION, {
        ...trackingListingProperties,
        listing_view: trackingIsGoldenScreen
          ? 'golden_screen'
          : showDetails
            ? 'experience_detail'
            : 'listing_detail',
        Duration: durationSeconds,
        duration_seconds: durationSeconds,
        exit_trigger: exitTrigger,
        hook_video_started: videoStartedRef.current,
        highest_video_progress: Math.max(0, ...videoProgressRef.current),
        golden_screen_viewed: goldenScreenViewedRef.current,
        price_viewed: experiencePriceViewedRef.current,
      });
    };

    const handlePageHide = () => trackListingDuration('pagehide');

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      trackListingDuration('navigation_or_view_change');
    };
  }, [
    trackingListingId,
    trackingListingTitle,
    trackingIsGoldenScreen,
    showDetails,
  ]);

  useEffect(() => {
    const video = goldenScreenVideo.current;
    if (!trackingListingId || !trackingListingTitle || !trackingIsGoldenScreen || !video) {
      return undefined;
    }

    const videoVariant =
      windowWidth >= 1080 ? 'desktop' : windowWidth >= 768 ? 'tablet' : 'mobile';

    const handlePlaying = () => {
      if (videoStartedRef.current) {
        return;
      }
      videoStartedRef.current = true;
      trackMixpanelEvent(MIXPANEL_EVENTS.HOOK_VIDEO_STARTED, {
        ...trackingListingProperties,
        video_variant: videoVariant,
        video_duration_seconds: Number.isFinite(video.duration)
          ? Math.round(video.duration)
          : null,
      });
    };

    const handleTimeUpdate = () => {
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        return;
      }

      const progress = (video.currentTime / video.duration) * 100;
      [25, 50, 75, 100].forEach(checkpoint => {
        // The hero video loops, so browsers may restart it just before reporting
        // an exact 100%. Treat 99% as a completed watch.
        const checkpointReached = checkpoint === 100 ? progress >= 99 : progress >= checkpoint;
        if (checkpointReached && !videoProgressRef.current.has(checkpoint)) {
          videoProgressRef.current.add(checkpoint);
          trackMixpanelEvent(MIXPANEL_EVENTS.HOOK_VIDEO_PROGRESS, {
            ...trackingListingProperties,
            progress_percent: checkpoint,
            seconds_watched: Math.round(video.currentTime),
            video_duration_seconds: Math.round(video.duration),
            video_variant: videoVariant,
          });
        }
      });
    };

    video.addEventListener('playing', handlePlaying);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleTimeUpdate);

    return () => {
      video.removeEventListener('playing', handlePlaying);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleTimeUpdate);
    };
  }, [trackingListingId, trackingListingTitle, trackingIsGoldenScreen, windowWidth]);

  useEffect(() => {
    const goldenScreenContent = goldenScreenContentRef.current;
    if (
      !trackingListingId ||
      !trackingListingTitle ||
      !trackingIsGoldenScreen ||
      !goldenScreenContent ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          goldenScreenViewedRef.current = true;
          const startedAt = funnelStartedAtRef.current;
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          trackMixpanelEvent(MIXPANEL_EVENTS.GOLDEN_SCREEN_VIEWED, {
            ...trackingListingProperties,
            seconds_since_listing_view: startedAt
              ? Math.round((now - startedAt) / 1000)
              : null,
            highest_video_progress: Math.max(0, ...videoProgressRef.current),
          });
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: '0px 0px -40% 0px' }
    );

    observer.observe(goldenScreenContent);
    return () => observer.disconnect();
  }, [trackingListingId, trackingListingTitle, trackingIsGoldenScreen]);

  useEffect(() => {
    if (!trackingListingId || !trackingListingTitle || !trackingIsGoldenScreen) {
      return undefined;
    }

    const trackListingAbandonment = () => {
      if (experienceDetailOpenedRef.current || listingAbandonmentTrackedRef.current) {
        return;
      }

      listingAbandonmentTrackedRef.current = true;
      const startedAt = funnelStartedAtRef.current;
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      const highestVideoProgress = Math.max(0, ...videoProgressRef.current);
      const lastStep = goldenScreenViewedRef.current
        ? 'golden_screen'
        : videoStartedRef.current
          ? 'hook_video'
          : 'listing';

      trackMixpanelEvent(MIXPANEL_EVENTS.FUNNEL_STEP_ABANDONED, {
        ...trackingListingProperties,
        last_step: lastStep,
        hook_video_started: videoStartedRef.current,
        highest_video_progress: highestVideoProgress,
        golden_screen_viewed: goldenScreenViewedRef.current,
        seconds_on_listing: startedAt
          ? Math.round((now - startedAt) / 1000)
          : null,
      });
    };

    window.addEventListener('pagehide', trackListingAbandonment);
    return () => {
      window.removeEventListener('pagehide', trackListingAbandonment);
      trackListingAbandonment();
    };
  }, [trackingListingId, trackingListingTitle, trackingIsGoldenScreen]);

  useEffect(() => {
    const priceSection = experiencePriceRef.current;
    if (
      !trackingListingId ||
      !trackingListingTitle ||
      trackingIsGoldenScreen ||
      !priceSection ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return undefined;
    }

    experiencePriceViewedRef.current = false;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting) && !experiencePriceViewedRef.current) {
          experiencePriceViewedRef.current = true;
          const startedAt = funnelStartedAtRef.current;
          const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
          const listingPrice = currentListing.attributes?.price;

          trackMixpanelEvent(MIXPANEL_EVENTS.PRICE_VIEWED, {
            ...trackingListingProperties,
            price_view_location: 'experience_detail',
            amount: listingPrice?.amount || null,
            currency: listingPrice?.currency || null,
            seconds_on_detail: startedAt
              ? Math.round((now - startedAt) / 1000)
              : null,
          });
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(priceSection);
    return () => observer.disconnect();
  }, [trackingListingId, trackingListingTitle, trackingIsGoldenScreen, showDetails]);

  useEffect(() => {
    if (!trackingListingId || !trackingListingTitle || trackingIsGoldenScreen) {
      return undefined;
    }

    const trackPriceAbandonment = () => {
      if (
        !experiencePriceViewedRef.current ||
        experienceContinuedRef.current ||
        priceAbandonmentTrackedRef.current
      ) {
        return;
      }

      priceAbandonmentTrackedRef.current = true;
      const startedAt = funnelStartedAtRef.current;
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      trackMixpanelEvent(MIXPANEL_EVENTS.FUNNEL_STEP_ABANDONED, {
        ...trackingListingProperties,
        last_step: 'experience_detail_price',
        price_view_location: 'experience_detail',
        seconds_on_detail: startedAt
          ? Math.round((now - startedAt) / 1000)
          : null,
      });
    };

    window.addEventListener('pagehide', trackPriceAbandonment);
    return () => {
      window.removeEventListener('pagehide', trackPriceAbandonment);
      trackPriceAbandonment();
    };
  }, [trackingListingId, trackingListingTitle, trackingIsGoldenScreen, showDetails]);

  const handleExperienceCtaClick = () => {
    const startedAt = funnelStartedAtRef.current;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();

    if (!trackingIsGoldenScreen) {
      experienceContinuedRef.current = true;
    } else {
      experienceDetailOpenedRef.current = true;
    }

    trackMixpanelEvent(
      trackingIsGoldenScreen
        ? MIXPANEL_EVENTS.EXPERIENCE_DETAIL_OPENED
        : MIXPANEL_EVENTS.EXPERIENCE_DETAIL_CONTINUED,
      {
        ...trackingListingProperties,
        seconds_on_detail: startedAt
          ? Math.round((now - startedAt) / 1000)
          : null,
        highest_video_progress: Math.max(0, ...videoProgressRef.current),
      }
    );
  };

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const splitTitle = titleToSplit => {
    if (!titleToSplit) {
      return {
        line1: intl.formatMessage({ id: 'ListingPageCarousel.heroTitle' }),
        line2: intl.formatMessage({ id: 'ListingPageCarousel.heroTitleLine2' }),
      };
    }
    const words = titleToSplit.split(' ');
    if (words.length <= 1) return { line1: titleToSplit, line2: '' };
    const mid = Math.ceil(words.length / 2);
    const line1 = words.slice(0, mid).join(' ');
    const line2 = words.slice(mid).join(' ');
    return { line1, line2 };
  };

  const { line1: heroTitleLine1, line2: heroTitleLine2 } = splitTitle(title);


  const { goldenSection = {}, detailsSection = {} } = publicData;
  const elopementEpRaw = publicData.elopementEp;
  const elopementEp = Number(elopementEpRaw);
  const showElopementEpisode =
    elopementEpRaw != null && elopementEpRaw !== '' && Number.isFinite(elopementEp);

  let customHeroImageUrl = null;
  let customHeroImageTabletUrl = null;
  let customHeroImageMobileUrl = null;

  if (publicData.screen_experience === 'golden_screen') {
    if (showDetails) {
      customHeroImageUrl = detailsSection?.heroImage?.url;
      customHeroImageTabletUrl = detailsSection?.heroImageTab?.url;
      customHeroImageMobileUrl = detailsSection?.heroImageMobile?.url;
    } else {
      customHeroImageUrl = goldenSection?.heroImage?.url;
      customHeroImageTabletUrl = goldenSection?.heroImageTab?.url;
      customHeroImageMobileUrl = goldenSection?.heroImageMobile?.url;
    }
  } else if (publicData.screen_experience === 'simple_screen') {
    customHeroImageUrl = detailsSection?.heroImage?.url;
    customHeroImageTabletUrl = detailsSection?.heroImageTab?.url;
    customHeroImageMobileUrl = detailsSection?.heroImageMobile?.url;
  }

  const heroImageUrl =
    customHeroImageUrl ||
    publicData.heroImageCloudinaryUrl ||
    require('../../assets/listingbackgroundcss.jpg');

  const heroImageTabletUrlOriginal =
    customHeroImageTabletUrl ||
    heroImageUrl;

  const heroImageMobileUrlOriginal =
    customHeroImageMobileUrl ||
    heroImageUrl;

  const isCloudinaryHero =
    heroImageUrl && typeof heroImageUrl === 'string' && heroImageUrl.includes('res.cloudinary.com');
  const optimizedHeroImageUrl = isCloudinaryHero
    ? optimizeCloudinaryUrl(heroImageUrl, 1920, '16:9', 'best')
    : heroImageUrl;
  const heroImageSrcSet = isCloudinaryHero
    ? getCloudinarySrcSet(heroImageUrl, '16:9', DESKTOP_HERO_SRCSET_WIDTHS, 'best')
    : null;

  // Tablet: 3:4 portrait crop — matches tablet aspect ratio
  const isCloudinaryTablet =
    heroImageTabletUrlOriginal && typeof heroImageTabletUrlOriginal === 'string' && heroImageTabletUrlOriginal.includes('res.cloudinary.com');
  const heroImageTabletUrl = isCloudinaryTablet
    ? optimizeCloudinaryUrl(heroImageTabletUrlOriginal, 1240, '3:4', 'best')
    : heroImageTabletUrlOriginal;
  const heroImageTabletSrcSet = isCloudinaryTablet
    ? getCloudinarySrcSet(heroImageTabletUrlOriginal, '3:4', TABLET_SRCSET_WIDTHS, 'best')
    : null;

  // Mobile: 9:16 aspect ratio — fills phone viewport height without cropping
  const isCloudinaryMobile =
    heroImageMobileUrlOriginal && typeof heroImageMobileUrlOriginal === 'string' && heroImageMobileUrlOriginal.includes('res.cloudinary.com');
  const heroImageMobileUrl = isCloudinaryMobile
    ? optimizeCloudinaryUrl(heroImageMobileUrlOriginal, 800, '9:16', 'best')
    : heroImageMobileUrlOriginal;
  const heroImageMobileSrcSet = isCloudinaryMobile
    ? getCloudinarySrcSet(heroImageMobileUrlOriginal, '9:16', MOBILE_SRCSET_WIDTHS, 'best')
    : null;
  const heroSubtitle =
    publicData.subtitle || intl.formatMessage({ id: 'ListingPageCarousel.heroSubtitle' });

  const listingDescription =
    publicData.screen_experience === 'golden_screen' && showDetails
      ? detailsSection.description || description
      : description;

  // Safely extract times from days
  const times =
    publicData.days?.flatMap(day => day.timeline?.map(item => item.startTime || item.time)) || [];

  const getDaysFromMinutes = minutes => {
    const hours = minutes / 60;

    if (hours < 24) return 0.5;

    return hours / 24;
  };

  const formatAmPmOnly = timeStr => {
    if (!timeStr) return 'AM';
    const str = String(timeStr).trim().toUpperCase();

    if (str === 'AM' || str === 'PM') return str;

    if (str.includes('AFTERNOON') || str.includes('EVENING') || str.includes('NIGHT') || str.includes('PM')) {
      return 'PM';
    }
    if (str.includes('MORNING') || str.includes('AM')) {
      return 'AM';
    }

    const match = str.match(/^(\d{1,2})[:.]?(\d{2})?/);
    if (match) {
      const hours = parseInt(match[1], 10);
      if (!isNaN(hours)) {
        return hours >= 12 ? 'PM' : 'AM';
      }
    }
    return 'AM';
  };

  const listingFields = config.listing.listingFields || [];
  const modeOfTransportField = listingFields.find(field => field.key === 'mode_of_transport');
  const enumOptions = modeOfTransportField?.enumOptions || [];

  const transportValues = Array.isArray(publicData.mode_of_transport)
    ? publicData.mode_of_transport
    : publicData.mode_of_transport
      ? [publicData.mode_of_transport]
      : [];

  const getOptionLabel = value => {
    const option = enumOptions.find(o => o.option === value);
    return option ? option.label : value;
  };

  const rawTransportMode = publicData?.transporationMode || publicData?.transportationMode || publicData?.transportation_mode;
  const modeOfTransportDisplay = rawTransportMode
    ? (Array.isArray(rawTransportMode) ? rawTransportMode.join(', ') : rawTransportMode)
    : (transportValues.length > 0
      ? (transportValues.length === 1
        ? getOptionLabel(transportValues[0])
        : `${getOptionLabel(transportValues[0])} +${transportValues.length - 1}`)
      : 'Land+');

  const startsAtDisplay = publicData.experienceStartTime
    ? formatAmPmOnly(publicData.experienceStartTime)
    : times.length > 0
      ? formatAmPmOnly(times[0])
      : 'AM';

  // Safely get booking length
  const bookingLengthInMinutes =
    publicData.bookingLengthInMinutes || publicData.priceVariants?.[0]?.bookingLengthInMinutes || 0;
  const days = bookingLengthInMinutes > 0 ? getDaysFromMinutes(bookingLengthInMinutes) : 0;

  const expType = publicData.experience_type;
  const tourTypeDisplay = publicData?.tourDays || '';

  // Extract pricing data from priceVariants
  const priceVariants = publicData.priceVariants || [];
  const firstVariant = priceVariants[0] || {};
  const isFlat = firstVariant && 'numberOfPeople' in firstVariant;
  const personPricing =
    publicData.personPricing && publicData.personPricing.length > 0
      ? publicData.personPricing
      : (isFlat ? priceVariants : firstVariant.personPricing) || [];
  const hasOnePersonPricingInVariants = personPricing.some(p => p.numberOfPeople === 1);

  const isSimpleScreen =
    publicData.screen_experience === 'simple_screen' ||
    (publicData.screen_experience === 'golden_screen' && showDetails);
  const isCustomScreen =
    publicData.screen_experience === 'simple_screen' ||
    publicData.screen_experience === 'golden_screen';

  // Helper function to format price from subunits
  const formatPriceFromSubunits = priceInSubunits => {
    const priceInDollars = priceInSubunits / 100;
    return `US $${priceInDollars.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const getDynamicMonthYear = () => {
    const date = new Date();
    const options = { month: 'long', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Get the listing price (Money object) for 1 person
  const listingPriceAmount = price?.amount || 0;
  const lowestPrice = listingPriceAmount;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);
  const showDescription = foundListingTypeConfig?.defaultListingFields?.description;

  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const isNegotiation = isNegotiationProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
      ? 'purchase'
      : isNegotiation
        ? 'negotiation'
        : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const authorNeedsPayoutDetails =
    ['booking', 'purchase'].includes(processType) || (isNegotiation && unitType === OFFER);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && authorNeedsPayoutDetails && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquire transition to inquiry state in booking, purchase and negotiation processes.
  // Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToMakeOfferPage = handleNavigateToMakeOfferPage({
    ...commonParams,
    getListing,
  });
  // This is to navigate to MakeOfferPage when InvokeNegotiationForm is submitted
  const onNavigateToRequestQuotePage = handleNavigateToRequestQuotePage({
    ...commonParams,
    getListing,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else if (isNegotiation && unitType === REQUEST) {
      onNavigateToMakeOfferPage(values);
    } else if (isNegotiation && unitType === OFFER) {
      onNavigateToRequestQuotePage(values);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const detailsHeroImageUrl = detailsSection?.heroImage?.url;
  const imageVariants = facebookImages.map(img => img.url);
  const schemaImages = [detailsHeroImageUrl, ...imageVariants].filter(Boolean);

  const aggregateRatingMaybe =
    reviews.length > 0
      ? {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: (
            reviews.reduce((sum, review) => sum + review.attributes.rating, 0) / reviews.length
          ).toFixed(1),
          reviewCount: reviews.length,
          bestRating: 5,
          worstRating: 1,
        },
      }
      : {};
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );

  // -------------------------------------------------------------------------
  // SEO: canonical URL + clean meta description
  // -------------------------------------------------------------------------
  // Canonical should NOT include query params (e.g. ?showDetails=true) so
  // Google treats the golden-screen "details" view as the same page as the
  // hero view, rather than as separate/duplicate content.
  const canonicalURL = `${config.marketplaceRootURL}${location.pathname}`;

  // Full URL (with query/hash) — used for og:url / schema url, where the
  // exact variant the user is viewing/sharing is meaningful.
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;

  // Never let an empty/lorem-ipsum description leak into <meta>, OG tags,
  // or JSON-LD. Always fall back to the hero subtitle / translated default.
  const metaDescription = buildMetaDescription(description, heroSubtitle);

  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  // -------------------------------------------------------------------------
  // SEO: noindex logic
  // -------------------------------------------------------------------------
  // A listing should never be indexed while it's closed, still in draft, or
  // pending approval — these are either private or incomplete pages that
  // would otherwise get crawled in the brief window before their redirect
  // fires, or if a bot bypasses the client-side redirect entirely.
  const noIndexMaybe =
    currentListing.attributes.state === LISTING_STATE_CLOSED ||
      isDraftVariant ||
      isPendingApprovalVariant
      ? { noIndex: true }
      : {};

  const isGoldenScreen = publicData.screen_experience === 'golden_screen' && !showDetails;

  const customDescriptionTitle =
    detailsSection?.descriptionTitle || publicData?.descriptionTitle || '';

  const simpleScreenImages =
    detailsSection?.images && detailsSection.images.length > 0
      ? detailsSection.images
      : publicData.cloudinaryImages || [];

  const goldenScreenImages =
    goldenSection?.images && goldenSection.images.length > 0
      ? goldenSection.images
      : publicData.cloudinaryImages || [];

  const detailsImages =
    detailsSection?.images && detailsSection.images.length > 0
      ? detailsSection.images
      : publicData.cloudinaryImages
        ? publicData.cloudinaryImages.filter(img => img.description && img.description.trim() !== '')
        : [];

  // Parse video list from publicData (stored as an array of objects)
  const videoArray = Array.isArray(publicData.video) ? publicData.video : [];
  const desktopVideoObj = videoArray.find(v => v.type === 'desktop');
  const tabVideoObj = videoArray.find(v => v.type === 'tab');
  const mobileVideoObj = videoArray.find(v => v.type === 'mobile');

  const desktopVideoUrl = desktopVideoObj?.mp4;
  const tabVideoUrl = tabVideoObj?.mp4;
  const mobileVideoUrl = mobileVideoObj?.mp4;
  const fallbackVideoUrl = desktopVideoUrl || tabVideoUrl || mobileVideoUrl;
  const hasVideo = videoArray.length > 0;

  // Helper to get active video object based on screen width
  // 3:4 (tab) for mid-range devices; desktop above 1420; mobile below 640
  const getActiveVideoObj = () => {
    if (windowWidth > 1260 && desktopVideoObj) {
      return desktopVideoObj;
    }
    if (windowWidth >= 640 && tabVideoObj) {
      return tabVideoObj;
    }
    if (mobileVideoObj) {
      return mobileVideoObj;
    }
    return desktopVideoObj || tabVideoObj || mobileVideoObj;
  };
  const activeVideoObj = getActiveVideoObj();

  const getActiveGoldenHeroVideo = () => {
    if (windowWidth >= 1080 && goldenSection?.heroVideo) {
      return goldenSection.heroVideo;
    }
    if (windowWidth >= 768 && goldenSection?.heroVideoTab) {
      return goldenSection.heroVideoTab;
    }
    if (goldenSection?.heroVideoMobile) {
      return goldenSection.heroVideoMobile;
    }
    return goldenSection?.heroVideo || goldenSection?.heroVideoTab || goldenSection?.heroVideoMobile;
  };
  const activeGoldenHeroVideo = getActiveGoldenHeroVideo();

  // Bottom CTA video: 3:4 (videoTab) between 640px and 1420px
  const getActiveDetailsVideo = () => {
    if (windowWidth > 1240 && detailsSection?.video) {
      return detailsSection.video;
    }
    if (windowWidth >= 640 && detailsSection?.videoTab) {
      return detailsSection.videoTab;
    }
    if (detailsSection?.videoMobile) {
      return detailsSection.videoMobile;
    }
    return detailsSection?.video || detailsSection?.videoTab || detailsSection?.videoMobile;
  };
  const activeDetailsVideo = getActiveDetailsVideo();

  // Resolve bottom video and bottom image conditionally based on screenExperience
  const isExpSimple = publicData.screen_experience === 'simple_screen';
  const isExpGolden = publicData.screen_experience === 'golden_screen';

  let bottomVideoObj = null;
  if (isExpSimple) {
    bottomVideoObj = activeDetailsVideo;
  } else if (isExpGolden) {
    bottomVideoObj = activeDetailsVideo;
  }

  const customBottomImageUrl = (isExpSimple || isExpGolden) ? detailsSection?.bottomImage?.url : null;
  const customBottomImageTabletUrl = (isExpSimple || isExpGolden) ? detailsSection?.bottomImageTab?.url : null;
  const customBottomImageMobileUrl = (isExpSimple || isExpGolden) ? detailsSection?.bottomImageMobile?.url : null;

  const bottomImageUrl =
    customBottomImageUrl ||
    NON_GOLDEN_SCREEN_CTA_BACKGROUND;

  const bottomImageTabletUrlOriginal =
    customBottomImageTabletUrl ||
    bottomImageUrl;

  const bottomImageMobileUrlOriginal =
    customBottomImageMobileUrl ||
    bottomImageUrl;

  const isCloudinaryBottom =
    bottomImageUrl && typeof bottomImageUrl === 'string' && bottomImageUrl.includes('res.cloudinary.com');
  const optimizedBottomImageUrl = isCloudinaryBottom
    ? optimizeCloudinaryUrl(bottomImageUrl, 1920, '16:9', 'best')
    : bottomImageUrl;
  const bottomImageSrcSet = isCloudinaryBottom
    ? getCloudinarySrcSet(bottomImageUrl, '16:9', DESKTOP_HERO_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryBottomTablet =
    bottomImageTabletUrlOriginal && typeof bottomImageTabletUrlOriginal === 'string' && bottomImageTabletUrlOriginal.includes('res.cloudinary.com');
  const bottomImageTabletUrl = isCloudinaryBottomTablet
    ? optimizeCloudinaryUrl(bottomImageTabletUrlOriginal, 1300, '3:4', 'best')
    : bottomImageTabletUrlOriginal;
  const bottomImageTabletSrcSet = isCloudinaryBottomTablet
    ? getCloudinarySrcSet(bottomImageTabletUrlOriginal, '3:4', TABLET_SRCSET_WIDTHS, 'best')
    : null;

  const isCloudinaryBottomMobile =
    bottomImageMobileUrlOriginal && typeof bottomImageMobileUrlOriginal === 'string' && bottomImageMobileUrlOriginal.includes('res.cloudinary.com');
  const bottomImageMobileUrl = isCloudinaryBottomMobile
    ? optimizeCloudinaryUrl(bottomImageMobileUrlOriginal, 800, '9:16', 'best')
    : bottomImageMobileUrlOriginal;
  const bottomImageMobileSrcSet = isCloudinaryBottomMobile
    ? getCloudinarySrcSet(bottomImageMobileUrlOriginal, '9:16', MOBILE_SRCSET_WIDTHS, 'best')
    : null;

  // If no bottomVideoObj is present, fall back to the old format (publicData.video)
  const finalBottomVideoObj = bottomVideoObj || activeVideoObj;
  const hasBottomVideo =
    !isGoldenScreen && !!(finalBottomVideoObj?.mp4 || finalBottomVideoObj?.playbackId);

  // If no bottomImageUrl is present, fall back to NON_GOLDEN_SCREEN_CTA_BACKGROUND
  const finalBottomImageUrl = !isGoldenScreen ? bottomImageUrl : null;

  const listingTopbar = (
    // Golden screen listings use the vertical mobile topbar; all others use the
    // default logo + burger (heroOverlay) layout.
    <TopbarContainer mobileVariant={isGoldenScreen ? 'vertical' : 'heroOverlay'} />
  );

  return (
    <>
      <SmoothScrollWrapper>
        <Page
          title={schemaTitle}
          scrollingDisabled={scrollingDisabled}
          author={authorDisplayName}
          description={metaDescription}
          openGraphType="product"
          facebookImages={facebookImages}
          twitterImages={twitterImages}
          {...noIndexMaybe}
          schema={{
            '@context': 'https://schema.org',
            '@type': 'Product',
            description: metaDescription,
            name: schemaTitle,
            image: schemaImages,
            url: productURL,
            brand: {
              '@type': 'Brand',
              name: marketplaceName,
            },
            ...aggregateRatingMaybe,
            offers: {
              '@type': 'Offer',
              url: productURL,
              priceCurrency: config.currency,
              ...priceForSchemaMaybe(price),
              ...availabilityMaybe,
            },
          }}
        >
          <Helmet>
            {/* Canonical: strip query params so ?showDetails=true and other
                variants of the same listing are never treated as duplicate
                pages by search engines. */}
            <link rel="canonical" href={canonicalURL} />
            <link rel="preconnect" href="https://res.cloudinary.com" />

            {/* Explicit OG/Twitter image sizing + card type. Facebook/LinkedIn
                weight og:image:width/height when deciding how to render the
                preview card (see the LinkedIn share screenshot: without this
                crawlers sometimes size/crop the preview poorly or skip it). */}
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta name="twitter:card" content="summary_large_image" />

            {heroImageUrl && typeof heroImageUrl === 'string' ? (
              isCloudinaryHero ? (
                <link
                  rel="preload"
                  as="image"
                  href={optimizedHeroImageUrl}
                  imagesrcset={heroImageSrcSet}
                  imagesizes="(max-width: 768px) 100vw, 1000px"
                  imageSrcSet={heroImageSrcSet}
                  imageSizes="(max-width: 768px) 100vw, 1000px"
                  fetchpriority="high"
                />
              ) : (
                <link rel="preload" as="image" href={heroImageUrl} fetchpriority="high" />
              )
            ) : null}
          </Helmet>

          <LayoutSingleColumn
            className={css.pageRoot}
            topbar={listingTopbar}
            footer={<FooterContainer />}
          >
            {showListingImage && (
              <>
                {isGoldenScreen ? (
                  <header className={css.goldenScreenHeroWrapper} ref={goldenScreenHeroWrapper}>
                    {activeGoldenHeroVideo ? (
                      <StreamVideo
                        videoObj={activeGoldenHeroVideo}
                        className={css.goldenScreenHeroVideo}
                        autoPlay
                        loop
                        muted
                        playsInline
                        ref={goldenScreenVideo}
                      />
                    ) : (
                      <picture key={optimizedHeroImageUrl || rawParams.id}>
                        {/* Mobile: 9:16 portrait crop */}
                        <source
                          media="(max-width: 600px)"
                          srcSet={heroImageMobileSrcSet || heroImageMobileUrl}
                          sizes="100vw"
                          width="600"
                          height="1067"
                        />
                        {/* Tablet: 3:4 portrait crop */}
                        <source
                          media="(max-width: 1024px)"
                          srcSet={heroImageTabletSrcSet || heroImageTabletUrl}
                          sizes="100vw"
                          width="1024"
                          height="1365"
                        />
                        {/* Desktop fallback: 16:9 landscape */}
                        <img
                          src={optimizedHeroImageUrl}
                          srcSet={heroImageSrcSet}
                          sizes="(max-width: 768px) 100vw, 1000px"
                          alt={
                            title || intl.formatMessage({ id: 'ListingPageCarousel.altHeroImage' })
                          }
                          className={css.goldenScreenHeroImage}
                          fetchPriority="high"
                          decoding="async"
                          width="1200"
                          height="675"
                        />
                      </picture>
                    )}
                    {/* <div className={css.goldenScreenHeroOverlay} aria-hidden="true" /> */}
                    <ScrollDown nextSectionId={'listing-content'} />
                    <div
                      className={css.goldenScreenHeroContent}
                      ref={isGoldenScreen ? goldenScreenTitlteHide : ''}
                    >
                      <HeroTitleReveal
                        key={`golden-hero-title-${rawParams.id}`}
                        delay={0.2}
                        duration={1.2}
                        fromYPercent={20}
                        markers={false}
                        staggerAmount={0.6}
                        immediate
                      >
                        <H1 rootClassName={css.goldenScreenHeroTitle}>
                          {heroTitleLine1}
                          {heroTitleLine2 ? (
                            <>
                              <br />
                              {heroTitleLine2}
                            </>
                          ) : null}
                        </H1>
                      </HeroTitleReveal>
                      <HeroInfoReveal
                        key={`golden-hero-subtitle-${rawParams.id}`}
                        delay={1.4}
                        duration={1.2}
                        staggerAmount={0.6}
                        fromYPercent={30}
                        immediate
                      >
                        <p className={css.goldenScreenHeroSubtitle}>{heroSubtitle}</p>
                      </HeroInfoReveal>
                    </div>

                    <NamedLink
                      name={isGoldenScreen ? 'ListingPage' : 'BookingFlowPage'}
                      params={{ slug: listingSlug, id: listingId.uuid }}
                      to={isGoldenScreen ? { search: '?showDetails=true' } : undefined}
                      className={css.goldenScreenHeroExploreCta}
                      onClick={handleExperienceCtaClick}
                    >
                      <div className={css.goldenScreenHeroExploreCtaContent}>
                        <IconCard brand="exploreRoundCta" />
                        <img src="/static/img/logo.svg" alt="Logo" />
                      </div>
                    </NamedLink>
                  </header>
                ) : (
                  <header className={css.staticHeroWrapper}>
                    <div className={css.heroOverlay} />
                    <picture key={optimizedHeroImageUrl || rawParams.id}>
                      {/* Mobile: 9:16 portrait crop */}
                      <source
                        media="(max-width: 600px)"
                        src={heroImageMobileUrl}
                        srcSet={heroImageMobileSrcSet}
                        sizes="100vw"
                        width="600"
                        height="1067"
                      />
                      {/* Tablet: 3:4 portrait crop */}
                      <source
                        media="(max-width: 1240px)"
                        src={heroImageTabletUrl}
                        srcSet={heroImageTabletSrcSet}
                        sizes="100vw"
                        width="1240"
                        height="1365"
                      />
                      {/* Desktop fallback: 16:9 landscape */}
                      <img
                        src={optimizedHeroImageUrl}
                        srcSet={heroImageSrcSet}
                        sizes="100vw"
                        alt={title || intl.formatMessage({ id: 'ListingPageCarousel.altHeroImage' })}
                        className={classNames(css.heroImage, (listingSlug === 'the-styled-elopement' && css.elopementImgFixes))}
                        fetchPriority="high"
                        decoding="async"
                        width="1920"
                        height="1080"
                      />
                    </picture>

                    {/* Overlay */}
                    <div className={css.overlay} aria-hidden="true" />
                    <ScrollDown nextSectionId={'stats-info'} />

                    {/* Content */}
                    <div className={css.heroContent}>
                      <HeroTitleReveal
                        key={`hero-title-${rawParams.id}`}
                        staggerAmount={0.3}
                        duration={0.8}
                        fromYPercent={50}
                        triggerStart="top 80%"
                        delay={0.4}
                        markers={false}
                        className={css.animatedTitle}
                      >
                        <H1 rootClassName={css.title}>
                          {heroTitleLine1}
                          {heroTitleLine2 ? <> {heroTitleLine2}</> : null}
                        </H1>
                      </HeroTitleReveal>

                      <HeroInfoReveal
                        key={`hero-subtitle-${rawParams.id}`}
                        delay={1}
                        duration={0.8}
                        fromYPercent={50}
                        staggerAmount={0.6}
                        markers={false}
                        triggerStart="0 80%"
                      >
                        <p className={css.subtitle}>{heroSubtitle}</p>
                      </HeroInfoReveal>

                      <HeroInfoReveal
                        key={`hero-reserve-${rawParams.id}`}
                        delay={1.6}
                        duration={0.8}
                        fromYPercent={60}
                        staggerAmount={0.6}
                        markers={false}
                        triggerStart="0 80%"
                      >
                        <NamedLink
                          name="BookingFlowPage"
                          params={{ slug: listingSlug, id: listingId.uuid }}
                          className={css.reserveText}
                          onClick={handleExperienceCtaClick}
                        >
                          <IconCard brand="rightArrow" />
                          <span className={css.reserveLabel}>
                            <FormattedMessage id="ListingPageCarousel.reserveExperience" />
                          </span>
                        </NamedLink>
                      </HeroInfoReveal>

                      <div className={css.buttonGroup}>
                        <button
                          type="button"
                          className={classNames(
                            css.primaryBtn,
                            travelPreference === 'been_here'
                              ? css.btnExpanded
                              : css.btnCollapsed
                          )}
                          onClick={() => handleTravelPreferenceSelect('been_here')}
                          disabled={travelPreferenceSaveInProgress}
                          aria-pressed={travelPreference === 'been_here'}
                        >
                          <FormattedMessage id="ListingPageCarousel.beenHereButton" />
                          <span
                            className={
                              travelPreference === 'been_here'
                                ? css.tickVisible
                                : css.tickHidden
                            }
                          >
                            <IconCard brand="tick" />
                          </span>
                        </button>
                        <button
                          type="button"
                          className={classNames(
                            css.secondaryBtn,
                            travelPreference === 'want_to_go'
                              ? css.btnExpanded
                              : css.btnCollapsed
                          )}
                          onClick={() => handleTravelPreferenceSelect('want_to_go')}
                          disabled={travelPreferenceSaveInProgress}
                          aria-pressed={travelPreference === 'want_to_go'}
                        >
                          <FormattedMessage id="ListingPageCarousel.wantToGoButton" />
                          <span
                            className={
                              travelPreference === 'want_to_go'
                                ? css.tickVisible
                                : css.tickHidden
                            }
                          >
                            <IconCard brand="tick" />
                          </span>
                        </button>
                        {showElopementEpisode ? (
                          <button type="button" className={css.elopmentBtnEx}>
                            Episode {elopementEp}
                          </button>
                        ) : null}
                      </div>

                      {showElopementEpisode ? (
                        <button
                          type="button"
                          className={classNames(css.elopmentBtnEx, css.elopmentBtnExMobile)}
                        >
                          Episode {elopementEp}
                        </button>
                      ) : null}
                    </div>
                  </header>
                )}


                <main id="listing-content">
                  {!isGoldenScreen && (
                    <section id="stats-info" className={css.heroInfoSection}>
                      <div className={css.heroInfoGrid}>
                        <HeroStatsRevealStandalone y={40} duration={1.5} delay={0}>
                          <div className={css.heroInfoItem}>
                            <span className={css.heroInfoLabel}>
                              <FormattedMessage id="ListingPageCarousel.tourTypeLabel" />
                            </span>
                            <span className={classNames(css.heroInfoValue, css.nowrap)}>{tourTypeDisplay}</span>
                          </div>
                        </HeroStatsRevealStandalone>

                        <HeroStatsRevealStandalone y={40} duration={1.5} delay={.2}>
                          <div className={css.heroInfoItem}>
                            <span className={css.heroInfoLabel}>
                              <FormattedMessage id="ListingPageCarousel.startsAtLabel" />
                            </span>
                            <span className={css.heroInfoValue}>{startsAtDisplay}</span>
                          </div>
                        </HeroStatsRevealStandalone>

                        <HeroStatsRevealStandalone y={40} duration={1.5} delay={0.4}>
                          <div className={css.heroInfoItem}>
                            <span className={css.heroInfoLabel}>
                              <FormattedMessage id="ListingPageCarousel.modeOfTransportLabel" />
                            </span>
                            <span className={css.heroInfoValue}>{modeOfTransportDisplay}</span>
                          </div>
                        </HeroStatsRevealStandalone>

                        <HeroStatsRevealStandalone y={40} duration={1.5} delay={.6}>
                          <div
                            ref={experiencePriceRef}
                            className={`${css.heroInfoItem} ${css.pricingInfoItem}`}
                            onMouseEnter={handleMouseEnterPricingCard}
                            onClick={handleTogglePricingHoverCard}
                          >
                            <div className={css.pricingLabelRow}>
                              <span className={css.heroInfoLabel}>
                                <FormattedMessage id="ListingPageCarousel.pricingLabel" />
                              </span>
                              <IconCard brand="info" />
                            </div>
                            <span className={css.heroInfoValue}>
                              {formatPriceFromSubunits(lowestPrice)}
                            </span>

                            {
                              expType === 'multiple_day' ? '' :
                                <span className={css.heroInfoSubtext}>
                                  <FormattedMessage id="ListingPageCarousel.onwardsLabel" />
                                </span>
                            }

                            <div
                              className={classNames(css.pricingHoverCard, {
                                [css.pricingHoverCardActive]: activeHoverCard,
                                [css.pricingHoverCardDismissed]: pricingHoverDismissed,
                              })}
                              onMouseEnter={handleMouseEnterPricingCard}
                              onMouseLeave={handleMouseLeaveHoverCard}
                              onClick={event => event.stopPropagation()}
                            >
                              <div className={css.titleNcloseBtn}>

                                <h3 className={css.pricingHoverTitle}>
                                  <FormattedMessage id="ListingPageCarousel.pricingHoverTitle" />
                                </h3>
                                <button
                                  type="button"
                                  className={css.closeBtn}
                                  onClick={handleClosePricingHoverCard}
                                  aria-label={intl.formatMessage({ id: 'Modal.closeModal' })}
                                >
                                  <IconCard brand="close" />
                                </button>
                              </div>
                              <p className={css.pricingHoverText}>
                                {expType === 'multiple_day' ? (
                                  "Pricing is based on venue. Each venue has 30 guests capacity."
                                ) : (
                                  ''
                                  // <FormattedMessage id="ListingPageCarousel.pricingHoverText" />
                                )}
                              </p>

                              <div className={css.pricingHoverRows}>
                                <>
                                  {/* Base price for 1 person from listing price, only if not already in personPricing */}
                                  {!hasOnePersonPricingInVariants && (
                                    <div className={css.pricingHoverRow}>
                                      <span>
                                        {expType === 'multiple_day' ? (
                                          elopementEp === 1 ? 'Praya palazzo' : elopementEp === 2 ? 'The Siam' : getTitleAfterHyphen(title)
                                        ) : (
                                          <>
                                            1 <FormattedMessage id="ListingPageCarousel.personLabel" />
                                          </>
                                        )}
                                      </span>
                                      <span className={css.pricingHoverPrice}>{formatPriceFromSubunits(listingPriceAmount)}</span>
                                    </div>
                                  )}
                                  {/* Dynamic pricing for 2, 3, 4+ people */}
                                  {personPricing.map((pricing, index) => {
                                    const unitPriceInSubunits = pricing.numberOfPeople
                                      ? Math.round(pricing.priceInSubunits / pricing.numberOfPeople)
                                      : pricing.its;
                                    const venueName =
                                      elopementEp === 1
                                        ? 'Praya palazzo'
                                        : elopementEp === 2
                                          ? 'The Siam'
                                          : getTitleAfterHyphen(title);
                                    const isMultipleDay = expType === 'multiple_day';
                                    const displayLabel = isMultipleDay && venueName ? venueName : null;

                                    return (
                                      <div key={index} className={css.pricingHoverRow}>
                                        <span>
                                          {displayLabel ? (
                                            displayLabel
                                          ) : (
                                            <>
                                              <strong>{pricing.numberOfPeople}</strong>
                                              {' '}
                                              <FormattedMessage
                                                id={
                                                  pricing.numberOfPeople === 1
                                                    ? 'ListingPageCarousel.personLabel'
                                                    : 'ListingPageCarousel.peopleLabel'
                                                }
                                              />
                                            </>
                                          )}
                                        </span>
                                        <span className={css.pricingHoverPrice}>{formatPriceFromSubunits(unitPriceInSubunits)}</span>
                                      </div>
                                    );
                                  })}
                                </>
                              </div>

                              {expType !== 'multiple_day' && (
                                <p className={css.pricingHoverNote}>
                                  <FormattedMessage id="ListingPageCarousel.pricingPerPersonNote" />
                                </p>
                              )}

                              <p className={css.pricingHoverFooter}>
                                <FormattedMessage
                                   id="ListingPageCarousel.pricingFooter"
                                  values={{
                                    email: chunks => (
                                      <a
                                        href="mailto:info@lamhasia.com"
                                        onClick={event => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          window.location.assign('mailto:info@lamhasia.com');
                                        }}
                                      >
                                        {chunks}
                                      </a>
                                    ),
                                  }}
                                />
                              </p>
                            </div>
                          </div>
                        </HeroStatsRevealStandalone>
                      </div>
                    </section>
                  )}

                  <section
                    id={isGoldenScreen ? 'golden-screen-content' : undefined}
                    ref={isGoldenScreen ? goldenScreenContentRef : undefined}
                    className={classNames(
                      css.historySection,
                      isGoldenScreen
                        ? css.goldenScreenHistorySection
                        : css.nonGoldenScreenHistorySection
                    )}
                    aria-labelledby={isGoldenScreen ? undefined : 'listing-history-heading'}
                  >
                    {!isGoldenScreen && (
                      <H2 rootClassName={css.historyHeading} id="listing-history-heading">
                        Unfold Experience
                      </H2>
                    )}

                    {
                      !isGoldenScreen && (
                        elopementEp === 1 ? <p className={css.historySubtitle}>Praya Palazzo</p> : elopementEp === 2 ? <p className={css.shiamText}>The Siam</p> : null
                      )
                    }

                    {listingDescription ? (
                      <ParagraphRevealStandalone
                        start="top 80%"
                        end="top 25%"
                        once={false}
                        staggerEach={0.2}
                        wordDuration={12}
                        delay={0}
                        contentKey={listingDescription}
                      >
                        <div className={classNames(css.historyDetailsWrapper, css.listingMarkup)}>
                          {listingMarkup(listingDescription, css.historyDetails)}
                        </div>
                      </ParagraphRevealStandalone>
                    ) : null}
                  </section>
                  {(publicData.screen_experience !== 'golden_screen' || showDetails) && (
                    <section
                      className={css.carousalSection}
                      aria-label={intl.formatMessage({
                        id: 'ListingPageCarousel.carouselAriaLabel',
                      })}
                    >
                      <HistoryCarousel
                        listing={currentListing}
                        images={
                          publicData.screen_experience === 'simple_screen'
                            ? simpleScreenImages
                            : publicData.screen_experience === 'golden_screen' && showDetails
                              ? detailsImages
                              : null
                        }
                      />
                    </section>
                  )}
                  {isGoldenScreen && goldenScreenImages && goldenScreenImages.length > 0 && (
                    <div>
                      <GoldenScreenGallery
                        images={goldenScreenImages.slice(0, 5)}
                        ariaLabel={intl.formatMessage({
                          id: 'ListingPageCarousel.goldenGalleryAriaLabel',
                        })}
                      />
                    </div>
                  )}
                  {isSimpleScreen && (
                    <section
                      className={css.experiencesSection}
                      aria-labelledby="listing-experiences-heading"
                    >
                      {simpleScreenImages && simpleScreenImages.length > 0 ? (
                        simpleScreenImages.map((image, idx) => (
                          <article
                            className={css.experienceItem}
                            key={image.id || idx}
                            style={{ marginBottom: '20px' }}
                          >
                            {image.description ? (
                              <ParagraphRevealStandalone
                                once={false}
                                start="top 75%"
                                end="top top"
                                staggerEach={0.2}
                                wordDuration={12}
                                delay={0}
                                contentKey={image.description}
                              >
                                <div className={css.listingMarkup}>
                                  {listingMarkup(image.description, css.experienceText)}
                                </div>
                              </ParagraphRevealStandalone>
                            ) : null}
                          </article>
                        ))
                      ) : (
                        <>
                          <article className={css.experienceItem}>
                            <p className={css.experienceText}>
                              <FormattedMessage id="ListingPageCarousel.wildlifeText" />
                            </p>
                          </article>

                          <article className={css.experienceItem}>
                            <p className={css.experienceText}>
                              <FormattedMessage id="ListingPageCarousel.waterfallsText" />
                            </p>
                          </article>

                          <article className={css.experienceItem}>
                            <p className={css.experienceText}>
                              <FormattedMessage id="ListingPageCarousel.vineyardsText" />
                            </p>
                          </article>
                        </>
                      )}
                    </section>
                  )}
                  <section
                    className={classNames(css.experienceCtaSection, {
                      [css.goldenScreenExperienceCtaSection]: isGoldenScreen,
                      [css.nonGoldenScreenExperienceCtaSection]: !isGoldenScreen,
                    })}
                    aria-label={intl.formatMessage({
                      id: 'ListingPageCarousel.experienceCtaLabel',
                    })}
                  >
                    {hasBottomVideo ? (
                      <StreamVideo
                        videoObj={finalBottomVideoObj}
                        className={css.ctaBackgroundVideo}
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    ) : (
                      !isGoldenScreen && finalBottomImageUrl && (
                        <picture>
                          {/* Mobile: 9:16 portrait crop */}
                          <source
                            media="(max-width: 600px)"
                            src={bottomImageMobileUrl}
                            srcSet={bottomImageMobileSrcSet}
                            sizes="100vw"
                            width="600"
                            height="1067"
                          />
                          {/* Tablet: 3:4 portrait crop */}
                          <source
                            media="(max-width: 1240px)"
                            src={bottomImageTabletUrl}
                            srcSet={bottomImageTabletSrcSet}
                            sizes="100vw"
                            width="1024"
                            height="1365"
                          />
                          {/* Desktop fallback: 16:9 landscape */}
                          <img
                            src={optimizedBottomImageUrl}
                            srcSet={bottomImageSrcSet}
                            sizes="100vw"
                            alt={
                              title || intl.formatMessage({ id: 'ListingPageCarousel.altHeroImage' })
                            }
                            className={css.ctaBackgroundImage}
                            fetchPriority="low"
                            decoding="async"
                            width="1920"
                            height="1080"
                          />
                        </picture>
                      )
                    )}

                    <div className={css.experienceCtaOverlay}>
                      <HeroTitleReveal
                        key={`cta-ghost-${rawParams.id}`}
                        duration={1.2}
                        delay={0.2}
                        fromYPercent={40}
                        triggerStart="0 70%"
                      >
                        <p
                          className={classNames(css.experienceGhostText, {
                            [css.goldenScreenExperienceCtaGhost]: isGoldenScreen,
                            [css.nonGoldenScreenExperienceCtaGhost]: !isGoldenScreen,
                          }, [
                            '6a68c4a6-f574-4ef9-a440-c785a4bad584',
                            '6a4f69bb-793e-4f11-be0d-029cbb89960a',
                          ].includes(listingId.uuid)
                            ? css.bespokectaBgText
                            : null)}
                        >
                          <FormattedMessage id="ListingPageCarousel.experienceCtaGhost" />
                        </p>
                      </HeroTitleReveal>

                      <MagneticCtaButton
                        listingSlug={listingSlug}
                        listingId={listingId.uuid}
                        isGoldenScreen={isGoldenScreen}
                        css={css}
                        onClick={handleExperienceCtaClick}
                        className={
                          [
                            '6a68c4a6-f574-4ef9-a440-c785a4bad584',
                            '6a4f69bb-793e-4f11-be0d-029cbb89960a',
                          ].includes(listingId.uuid)
                            ? css.bespokeCtaMove
                            : null
                        }
                      />
                    </div>
                  </section>
                </main>
              </>
            )}

            {false && (
              <div className={css.listingInfo}>
                <CustomListingFields
                  publicData={publicData}
                  metadata={metadata}
                  listingFieldConfigs={listingConfig.listingFields}
                  categoryConfiguration={config.categoryConfiguration}
                  intl={intl}
                />

                <SectionMapMaybe
                  geolocation={geolocation}
                  publicData={publicData}
                  listingId={currentListing.id}
                  mapsConfig={config.maps}
                />
                <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
                <SectionAuthorMaybe
                  title={title}
                  listing={currentListing}
                  authorDisplayName={authorDisplayName}
                  onContactUser={onContactUser}
                  isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                  onCloseInquiryModal={() => setInquiryModalOpen(false)}
                  sendInquiryError={sendInquiryError}
                  sendInquiryInProgress={sendInquiryInProgress}
                  onSubmitInquiry={onSubmitInquiry}
                  currentUser={currentUser}
                  onManageDisableScrolling={onManageDisableScrolling}
                />
              </div>
            )}

            {false && (
              <div className={css.contentWrapperForProductLayout}>
                <div className={css.mainColumnForProductLayout}>
                  {mounted &&
                    currentListing.id &&
                    noPayoutDetailsSetWithOwnListing &&
                    !isCustomScreen ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                      currentUser={currentUser}
                    />
                  ) : null}
                  {mounted && currentListing.id && !isCustomScreen ? (
                    <ActionBarMaybe
                      className={css.actionBarForProductLayout}
                      isOwnListing={isOwnListing}
                      listing={currentListing}
                      currentUser={currentUser}
                      editParams={{
                        id: listingId.uuid,
                        slug: listingSlug,
                        type: listingPathParamType,
                        tab: listingTab,
                      }}
                    />
                  ) : null}
                  <div
                    className={
                      showListingImage ? css.mobileHeading : css.noListingImageHeadingProduct
                    }
                  ></div>
                </div>
              </div>
            )}
          </LayoutSingleColumn>
        </Page>
      </SmoothScrollWrapper >
    </>
  );
};

/**
 * The ListingPage component with carousel layout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Object} props.currentUser - The current user
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error
 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onUpdateProfile: data => dispatch(updateProfile(data)),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
